import { ref } from 'vue';
import { supabase } from '@/services/supabase';
import { useTasks } from './useTasks';

export function useEmailReminder() {
  // State
  const showEmailModal = ref(false);
  const currentTaskData = ref<{ documentId: string; documentName: string; processName: string; firstName: string; lastName: string; email: string; action: string, forecastPeriod: string }>({
    documentId: '',
    documentName: '',
    processName: '',
    firstName: '',
    lastName: '',
    email: '',
    action: '',
    forecastPeriod: '',
  });

  // Get document details from document_profile view
  const fetchDocumentDetails = async (documentId: string) => {
    try {
      const { data, error } = await supabase
        .from('document_profile')
        .select('*')
        .eq('id', documentId)
        .single();
      
      if (error) {
        console.error('Error fetching document details:', error);
        return null;
      }
      
      return data;
    } catch (err) {
      console.error('Exception in fetchDocumentDetails:', err);
      return null;
    }
  };

  // Show task message - now opens the email modal
  const showEmailReminderModal = async (documentId: string, userName: string, userEmail?: string | null, action: string = 'DOWNLOAD') => {
    // First, try to fetch the full document details
    const documentDetails = await fetchDocumentDetails(documentId);
    
    // Check if user email exists
    if (userEmail) {
      // Set the current task data with the document details and user email
      currentTaskData.value = {
        documentId: documentId,
        documentName: documentDetails?.name || 'Document ' + documentId,
        processName: documentDetails?.process_name || 'Forecasting Process',
        firstName: userName.split(' ')[0],
        lastName: userName.split(' ')[1] || '',
        email: userEmail,
        action: action,
        forecastPeriod: documentDetails?.process_name || 'Forecasting Process',
      };
      
      showEmailModal.value = true;
    } else {
      // Log error if user email is missing
      console.error('User email not found:', {
        documentId,
        userName,
        action
      });
    }
  };

  // Close email modal
  const closeEmailModal = () => {
    showEmailModal.value = false;
  };

  // Handle email send success
  const handleEmailSendSuccess = async () => {
    console.log('Email sent successfully');
    
    // Create a task record for the reminder
    try {
      // Get the current user session directly from supabase
      const { data: sessionData } = await supabase.auth.getSession();
      const currentUserId = sessionData.session?.user?.id;
      
      if (currentUserId && currentTaskData.value) {
        const now = new Date().toISOString();
        const tasksComposable = useTasks();
        
        // Create a new task with REMIND action
        await tasksComposable.createTask({
          due_at: now,
          done_at: now,
          action: 'REMIND',
          assigned_user_id: currentUserId,
          document_id: currentTaskData.value.documentId,
          description: `Reminder to download ${currentTaskData.value.documentName} sent to ${currentTaskData.value.email}`
        });
        
        console.log('Reminder task created successfully');
        
        // Dispatch a custom event to notify that tasks need to be refreshed
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('refresh-tasks'));
        }
      } else {
        console.error('Could not create reminder task: User or task data not available');
      }
    } catch (error) {
      console.error('Error creating reminder task:', error);
    }
    
    showEmailModal.value = false;
  };

  return {
    showEmailModal,
    currentTaskData,
    showEmailReminderModal,
    closeEmailModal,
    handleEmailSendSuccess
  };
}